/**
 * PlatformClinicsCard — read-only directory of every tenant on the platform.
 * Visible only to platform_admin role; shows Solo/Multi/legacy plan badges,
 * trial status, and a one-click "Open" deep link to the tenant's
 * /platform/clinics admin view for moderation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Globe2, Search, Building2, Sparkles, Crown, ExternalLink, ShieldCheck, AlertCircle } from 'lucide-react';

export default function PlatformClinicsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | solo | multi | unverified

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Platform admins can see unverified tenants too.
        const { data } = await api.get('/public/clinics?include_unverified=true&limit=500');
        setRows(data || []);
      } catch (_) { setRows([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filter === 'solo' && r.plan !== 'solo') return false;
      if (filter === 'multi' && r.plan !== 'multi') return false;
      if (filter === 'unverified' && r.email_verified !== false) return false;
      if (q) {
        const Q = q.toLowerCase();
        if (![r.name, r.slug, r.address].some(x => (x || '').toLowerCase().includes(Q))) return false;
      }
      return true;
    });
  }, [rows, q, filter]);

  const counts = useMemo(() => ({
    all: rows.length,
    solo: rows.filter(r => r.plan === 'solo').length,
    multi: rows.filter(r => r.plan === 'multi').length,
    unverified: rows.filter(r => r.email_verified === false).length,
  }), [rows]);

  return (
    <div className="admin-card md:col-span-2" data-testid="platform-clinics-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-[#FFB000]"/>
          <h3 className="font-heading font-semibold">All clinics on the platform</h3>
          <span className="text-[10px] uppercase tracking-wider bg-[#FFB000]/15 text-[#B8860B] px-1.5 py-0.5 rounded font-medium">Platform admin</span>
        </div>
        <Link to="/platform/clinics" className="text-xs text-brand hover:underline inline-flex items-center gap-1" data-testid="open-platform-clinics">
          Open full directory <ExternalLink className="w-3 h-3"/>
        </Link>
      </div>
      <p className="text-xs text-fg-muted mb-3">
        Every tenant on Physiogenic at a glance. Solo (lone practitioner) vs Multi (branches), verification status,
        and a deep link to the per-clinic admin page.
      </p>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted"/>
          <input className="input pl-7 w-full text-sm" placeholder="Search clinic name, slug, address…"
                 value={q} onChange={e=>setQ(e.target.value)} data-testid="platform-clinics-search"/>
        </div>
        <div className="flex items-center gap-1" key={`tabs-${rows.length}`}>
          {[
            ['all', 'All', counts.all],
            ['solo', 'Solo', counts.solo],
            ['multi', 'Multi', counts.multi],
            ['unverified', 'Unverified', counts.unverified],
          ].map(([k, label, count]) => (
            <button key={k} onClick={()=>setFilter(k)}
                    className={`px-2.5 py-1 text-xs rounded-md border transition ${filter === k ? 'bg-brand text-white border-brand' : 'border-soft text-fg-muted hover:border-brand/40'}`}
                    data-testid={`platform-clinics-filter-${k}`}>
              {label} <span className="opacity-70">({count})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-fg-muted">Loading platform directory…</div>
      ) : filtered.length === 0 ? (
        <div className="surface border border-soft rounded-lg p-4 text-sm text-fg-muted text-center">
          No clinics match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-fg-muted border-b border-soft">
                <th className="py-2 pr-3">Clinic</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3">Verified</th>
                <th className="py-2 pr-3">Country</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.clinic_id} className="border-b border-soft/60 hover:bg-[#FAF6EE]/40">
                  <td className="py-2 pr-3">
                    <div className="font-medium text-fg">{r.name}</div>
                    {r.slug && <div className="text-[11px] text-fg-muted font-mono">/{r.slug}</div>}
                  </td>
                  <td className="py-2 pr-3">
                    {r.plan === 'multi' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand/10 text-brand text-[11px] font-medium">
                        <Building2 className="w-3 h-3"/> Multi
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#FFB000]/15 text-[#B8860B] text-[11px] font-medium">
                        <Sparkles className="w-3 h-3"/> Solo
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {r.email_verified === false ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#B83A3A]">
                        <AlertCircle className="w-3 h-3"/> Unverified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#1B5E20]">
                        <ShieldCheck className="w-3 h-3"/> Verified
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-fg-muted">
                    {r.country_code || '—'}
                  </td>
                  <td className="py-2 pr-3">
                    {r.slug && (
                      <Link to={`/c/${r.slug}`} className="text-[11px] text-brand hover:underline inline-flex items-center gap-0.5">
                        Public page <Globe2 className="w-3 h-3"/>
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
